import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import CargomaticCard from 'components/v2/card';
import OrdersActionCreators from 'actions/OrdersActionCreators';
import OrdersAccountingStore from 'stores/OrdersAccountingStore';
import formatMoney from 'utils/formatters/Money';
import { getDocumentsV2, getSignedUrls } from 'utils/DocumentsAPIUtils';
import GenerateLedgerModal from './GenerateLedgerModal';
import { getUserInvoiceSettings } from 'utils/UserServiceAPIUtils';
import ChargeItem from './ChargeItem';
import { RECEIVABLES, PAYABLES } from '../../../../constants/LedgerTypes';
function needsProcessing(ledgerType, shipment) {
    if (ledgerType === 'receivables') {
        return shipment.needs_invoice;
    }
    return shipment.needs_payout;
}
class Accounting extends Component {
    constructor(props) {
        super(props);
        this.fetchShipperInvoiceSettings = async () => {
            var _a, _b;
            const shipper = this.props.order.shipper;
            const shipperId = shipper._id ? shipper._id : shipper;
            if (shipperId) {
                try {
                    const invoiceSettingsResult = await getUserInvoiceSettings(shipperId);
                    const invoiceSettings = (_b = (_a = invoiceSettingsResult === null || invoiceSettingsResult === void 0 ? void 0 : invoiceSettingsResult.data) === null || _a === void 0 ? void 0 : _a.getInvoiceSetting) === null || _b === void 0 ? void 0 : _b.invoice_setting;
                    this.setState({
                        shipperInvoiceSettings: invoiceSettings
                    });
                }
                catch (e) {
                    this.setState({
                        shipperInvoiceSettings: null,
                    });
                    console.error(e);
                }
            }
            else {
                console.error('SHIPPER_NOT_FOUND_ON_ORDER');
            }
        };
        this.handleOrdersAccountingStoreChange = () => {
            this.setState({
                paymentPending: false,
            });
        };
        this.handleGenerateLedger = (ledgerDate, selectedCharges, sendEdi = false, sendEmail = false) => {
            const { order: { _id: orderId }, ledgerType, } = this.props;
            this.setState({
                paymentPending: true,
                showGenerateLedgerModal: false,
            });
            if (ledgerType === RECEIVABLES) {
                OrdersActionCreators.generateOrderInvoice(orderId, ledgerDate, selectedCharges, sendEdi, sendEmail);
            }
            else {
                OrdersActionCreators.generateOrderPayout(orderId, ledgerDate, selectedCharges, true);
            }
        };
        this.handleOpenGenerateLedgerModal = () => this.setState({ showGenerateLedgerModal: true });
        this.handleCloseGenerateLedgerModal = () => this.setState({ showGenerateLedgerModal: false });
        this.state = {
            paymentPending: false,
            showGenerateLedgerModal: false,
            shipperInvoiceSettings: null,
            pdfUrls: {},
        };
    }
    async componentDidMount() {
        OrdersAccountingStore.addChangeListener(this.handleOrdersAccountingStoreChange);
        this.fetchSignedUrls(this.props.order);
        await this.fetchShipperInvoiceSettings();
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        this.fetchSignedUrls(nextProps.order);
    }
    componentWillUnmount() {
        OrdersAccountingStore.removeChangeListener(this.handleOrdersAccountingStoreChange);
    }
    async fetchSignedUrls(order) {
        var _a, _b;
        try {
            const pdfUrls = {};
            let changed = false;
            const ids = [
                ...order.invoices.map((invoice) => invoice._id),
                ...order.payouts.map((payout) => payout._id),
            ];
            if (!ids.length) {
                return;
            }
            const getDocumentsResponse = await getDocumentsV2(ids);
            const documents = (_a = getDocumentsResponse.data) === null || _a === void 0 ? void 0 : _a.getDocumentsV2.documents;
            const getSignedUrlsResponse = await getSignedUrls(ids);
            const signedUrls = (_b = getSignedUrlsResponse.data) === null || _b === void 0 ? void 0 : _b.getSignedUrls.signedUrls;
            documents.forEach((document) => {
                document.foreignKeys.forEach((ledgerId) => {
                    var _a;
                    if (((_a = this.state.pdfUrls[ledgerId]) === null || _a === void 0 ? void 0 : _a.createdAt) !== document.createdAt) {
                        changed = true;
                        pdfUrls[ledgerId] = {
                            signedUrl: signedUrls.find((signedUrl) => document.id === signedUrl.id).signedUrl,
                            createdAt: document.createdAt,
                        };
                    }
                });
            });
            if (changed) {
                this.setState({ pdfUrls });
            }
        }
        catch (e) {
            console.error('orders/accounting, failed to get signed URL: ', e);
        }
    }
    getOutstandingTotal() {
        const { ledgerType, order } = this.props;
        if (ledgerType === 'receivables') {
            return order.outstanding_ar;
        }
        return order.outstanding_ap;
    }
    getChargeItems() {
        const { ledgerType, shipments } = this.props;
        return shipments.filter((job) => needsProcessing(ledgerType, job) && !job.canceled);
    }
    getLedgerType() {
        const { ledgerType, t } = this.props;
        if (ledgerType === RECEIVABLES) {
            return t('INVOICE_NUMBER');
        }
        return t('PAYOUT_NUMBER');
    }
    getInvoices() {
        const { order } = this.props;
        return order.invoices || [];
    }
    getPayouts() {
        const { order } = this.props;
        return order.payouts || [];
    }
    getLedgerItems() {
        const { ledgerType } = this.props;
        let ledgerItems = [];
        if (ledgerType === RECEIVABLES) {
            ledgerItems = ledgerItems.concat(this.getInvoices());
        }
        else {
            ledgerItems = ledgerItems.concat(this.getPayouts());
        }
        return ledgerItems;
    }
    getPaymentButtonText() {
        const { ledgerType, t } = this.props;
        const action = ledgerType === RECEIVABLES ? 'INVOICE' : 'PAYOUT';
        return t('CREATE_WITH_VALUE', { action: t(action, { count: 1 }) });
    }
    getSectionTitle() {
        const { ledgerType, t } = this.props;
        if (ledgerType === RECEIVABLES) {
            return t('ACCOUNT_RECEIVABLE');
        }
        return t('ACCOUNT_PAYABLE');
    }
    paymentActionsEnabled() {
        const { ledgerType } = this.props;
        const shipments = this.props.shipments || [];
        const field = ledgerType === RECEIVABLES ? 'needs_invoice' : 'needs_payout';
        return shipments.find((s) => s[field] && !s.canceled) !== undefined;
    }
    renderPaymentActions() {
        var _a;
        if (!this.paymentActionsEnabled()) {
            return null;
        }
        const outstandingTotal = this.getOutstandingTotal();
        const { t } = this.props;
        return (React.createElement("div", { className: "flex items-center justify-between mb-4 payment-actions" },
            React.createElement("div", null,
                React.createElement("label", { className: "mr-2" },
                    t('TOTAL_OUTSTANDING'),
                    ":"),
                React.createElement("span", { className: "text-sm" }, formatMoney(outstandingTotal, (_a = this.props.order) === null || _a === void 0 ? void 0 : _a.currency))),
            React.createElement("button", { className: "button button-hollow-green", disabled: this.state.paymentPending, onClick: this.handleOpenGenerateLedgerModal },
                this.state.paymentPending && (React.createElement("i", { className: `fa fa-circle-o-notch fa-spin ${RECEIVABLES === 'Invoice'
                        ? 'accounting-accounting-createInvoice heap'
                        : 'accounting-accounting-createPayout heap'}` })),
                this.getPaymentButtonText())));
    }
    renderLedgers() {
        const { ledgerType, t } = this.props;
        const ledgerItems = this.getLedgerItems();
        if (ledgerItems.length === 0) {
            return null;
        }
        const baseUrl = ledgerType === 'receivables' ? '/invoices' : '/payouts';
        return (React.createElement("table", { className: "table table-striped table-hover" },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", null, this.getLedgerType()),
                    React.createElement("th", null, "\u00A0"),
                    React.createElement("th", null, "\u00A0"))),
            React.createElement("tbody", null, ledgerItems.map((ledger) => {
                var _a;
                const ledgerDetailLink = `${baseUrl}/${ledger._id}`;
                const pdf = (_a = this.state.pdfUrls[ledger._id]) === null || _a === void 0 ? void 0 : _a.signedUrl;
                const emailSent = ledgerType === 'receivables' && ledger.email_sent;
                return (React.createElement("tr", { key: ledger._id },
                    React.createElement("th", null,
                        React.createElement("a", { href: ledgerDetailLink }, ledger.number)),
                    React.createElement("th", null, ledger.edi_sent && (React.createElement("span", { className: "p-1 text-sm text-white rounded bg-green" }, t('EDI_SENT')))),
                    React.createElement("th", null, emailSent && (React.createElement("span", { className: "p-1 text-sm text-white rounded bg-green" }, t('INVOICE_SENT')))),
                    React.createElement("th", null,
                        pdf && (React.createElement("a", { href: pdf, target: "_blank", rel: "noopener noreferrer" }, t('DOWNLOAD'))),
                        !pdf && React.createElement("span", null))));
            }))));
    }
    render() {
        const { ledgerType } = this.props;
        return (React.createElement(CargomaticCard, { header: () => React.createElement("h3", { className: "my-0" }, this.getSectionTitle()) },
            React.createElement("div", { className: "w-full" },
                this.renderPaymentActions(),
                this.getChargeItems().map((shipment, i) => {
                    var _a;
                    return (React.createElement(ChargeItem, { isEven: (i + 1) % 2 === 0, key: shipment._id, shipment: shipment, ledgerType: ledgerType, currency: (_a = this.props.order) === null || _a === void 0 ? void 0 : _a.currency }));
                }),
                this.renderLedgers()),
            this.state.showGenerateLedgerModal && (React.createElement(GenerateLedgerModal, { show: this.state.showGenerateLedgerModal, equipment: this.props.order.equipments, shipper: this.props.order.shipper, carrier: this.props.order.carrier, orderId: this.props.order._id, shipments: this.props.shipments, ledgerType: ledgerType, onGenerateLedger: this.handleGenerateLedger, onClose: this.handleCloseGenerateLedgerModal, shipperInvoiceSettings: this.state.shipperInvoiceSettings }))));
    }
}
Accounting.propTypes = {
    order: PropTypes.object,
    ledgerType: PropTypes.oneOf([RECEIVABLES, PAYABLES]),
};
export default withTranslation()(Accounting);
